import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiX, FiPlus } from 'react-icons/fi';
import api from '../../services/api';
import Alert from '../Common/Alert';
import StarRating from '../Common/StarRating';
import SiteRatingModal from './SiteRatingModal';
import AiSuggestedSiteCriteriaModal from './AiSuggestedSiteCriteriaModal';
import AddNewSiteModal from './AddNewSiteModal';
import AddInvestigatorModal from './AddInvestigatorModal';
import { ElectronicSign } from '../Common/ElectronicSign';
import MultiSelectDropdown from '../Common/MultiSelectDropdown';
import { sites as siteMessages } from '../../utils/validationMessages';
import { isLikelyPrincipalInvestigatorPersonName } from '../../utils/principalInvestigatorNameHeuristic';
import {
  shouldSuppressAsRawAiSitesPayload,
  resolveAiSuggestedSitesUserFacingMessage,
} from '../../utils/aiSuggestedSitesResponse';
import './SiteSelectionModal.css';

const SiteSelectionModal = ({ project, onClose, onSave, onAddNewSite }) => {
  const [activeTab, setActiveTab] = useState('proposed');
  const [selectedSites, setSelectedSites] = useState([]);
  const [suggestedSites, setSuggestedSites] = useState([]);
  const [alreadySelectedSiteIds, setAlreadySelectedSiteIds] = useState([]); // Sites already selected/active
  const [loading, setLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedSiteForRating, setSelectedSiteForRating] = useState(null);
  const [selectedAiSiteForCriteria, setSelectedAiSiteForCriteria] = useState(null);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showInvestigatorModal, setShowInvestigatorModal] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [toast, setToast] = useState(null);
  const eSignRemarksRef = useRef('');
  const eSignModeRef = useRef('proposed');

  // AI Suggested Sites state
  const [aiSites, setAiSites] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiRawResponse, setAiRawResponse] = useState(null);
  /** When true, last AI fetch showed integrated-source error toast only (hide inline raw + generic empty). */
  const [aiIntegratedSourceErrorOnly, setAiIntegratedSourceErrorOnly] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFetched, setAiFetched] = useState(false);
  const [aiDisease, setAiDisease] = useState('');
  const [aiTherapeuticArea, setAiTherapeuticArea] = useState('');
  const [selectedAiSiteIndices, setSelectedAiSiteIndices] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountryNos, setSelectedCountryNos] = useState([]);
  const [addNewSiteInitialData, setAddNewSiteInitialData] = useState(null);
  const [proposedSearchText, setProposedSearchText] = useState('');
  const [recommendedSearchText, setRecommendedSearchText] = useState('');
  const aiLastFetchCountsRef = useRef({ beforePiFilter: 0, afterPiFilter: 0 });

  const DEFAULT_AI_RATING_PARAMETERS = [
    'Disease Research Activity',
    'Investigator Expertise',
    'Clinical Trial History',
    'Infrastructure',
    'Patient Pool',
  ];

  const getOverallRatingTooltip = (site, isAiSuggested = false) => {
    const fromArray = [
      ...(Array.isArray(site?.ratingParameters) ? site.ratingParameters : []),
      ...(Array.isArray(site?.ratingParameterNames) ? site.ratingParameterNames : []),
      ...(Array.isArray(site?.scoreParameters) ? site.scoreParameters : []),
    ]
      .map((x) => String(x || '').trim())
      .filter(Boolean);

    if (fromArray.length > 0) {
      return `Rating Parameters: ${[...new Set(fromArray)].join(', ')}`;
    }

    if (isAiSuggested) {
      const fromExpertise = String(site?.therapeuticExpertise || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      if (fromExpertise.length > 0) {
        return `Rating Parameters: ${[...new Set(fromExpertise)].join(', ')}`;
      }
      return `Rating Parameters: ${DEFAULT_AI_RATING_PARAMETERS.join(', ')}`;
    }

    return 'Overall rating based on configured site criteria.';
  };

  useEffect(() => {
    fetchSites();
    fetchAlreadySelectedSites();
    fetchCountries();
  }, [project]);

  // Drop from pending selection any row that shows green (already in SiteMst / feasibility selected).
  useEffect(() => {
    setSelectedSites((prev) =>
      prev.filter((s) => {
        if (alreadySelectedSiteIds.includes(s.siteId)) {
          return false;
        }
        const sug = suggestedSites.find((x) => x.siteId === s.siteId);
        if (sug?.highlighted) {
          return false;
        }
        return true;
      })
    );
  }, [suggestedSites, alreadySelectedSiteIds]);

  const fetchCountries = async () => {
    try {
      const response = await api.get('/countries');
      const options = (response.data || [])
        .map((country) => ({
          value: String(country.countryNo),
          label: country.countryNiceName || country.countryName || '',
        }))
        .filter((country) => country.value && country.label);
      setCountryOptions(options);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setCountryOptions([]);
    }
  };

  const fetchAlreadySelectedSites = async () => {
    try {
      const response = await api.get(`/sites/feasibility/selected?project=${project}`);
      const selectedIds = response.data.map(site => site.siteId);
      setAlreadySelectedSiteIds(selectedIds);
    } catch (err) {
      console.error('Error fetching already selected sites:', err);
      setAlreadySelectedSiteIds([]);
    }
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      const suggestedResponse = await api.get(`/sites/feasibility/suggested?project=${project}`);
      const suggested = suggestedResponse.data.map(site => ({
        siteId: site.siteId,
        siteNo: site.siteNo || site.siteId,
        piName: site.piName || '',
        siteName: site.siteName || '',
        siteAddress: site.siteAddress || '',
        therapeuticArea: site.therapeuticArea || 'N/A',
        disease: site.disease || 'N/A',
        overallRating: site.overallRating || 0,
        highlighted: site.highlighted || false,
        ratingCriteria: site.ratingCriteria || site.RatingCriteria || null,
      }));
      setSuggestedSites(suggested);
    } catch (err) {
      console.error('Error fetching sites:', err);
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          err.message ||
                          'Failed to load sites';
      setSuggestedSites([]);
      setToast({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (siteId, checked) => {
    if (checked) {
      const site = suggestedSites.find(s => s.siteId === siteId);
      if (site && !selectedSites.find(s => s.siteId === siteId)) {
        setSelectedSites([...selectedSites, { ...site, siteNo: siteId }]);
      }
    } else {
      setSelectedSites(selectedSites.filter(s => s.siteId !== siteId));
    }
  };

  const isSiteSelected = (siteId) => {
    return selectedSites.some(s => s.siteId === siteId);
  };

  const handleViewRating = (site) => {
    setSelectedSiteForRating(site);
    setShowRatingModal(true);
  };

  const handleViewAiSuggestedCriteria = (site) => {
    setSelectedAiSiteForCriteria(site);
  };

  const isAiSiteSelected = (idx) => selectedAiSiteIndices.includes(idx);

  const handleAiCheckboxChange = (idx, checked) => {
    if (checked) {
      setSelectedAiSiteIndices(prev => (prev.includes(idx) ? prev : [...prev, idx]));
    } else {
      setSelectedAiSiteIndices(prev => prev.filter(i => i !== idx));
    }
  };

  const handleSave = () => {
    if (activeTab === 'recommended' && selectedAiSiteIndices.length > 0) {
      handleSaveAiSuggestedSites();
      return;
    }
    if (selectedSites.length > 0) {
      eSignRemarksRef.current = '';
      setShowESignModal(true);
    } else {
      setToast({ type: 'info', message: 'Please select at least one site' });
    }
  };

  const handleSaveAiSuggestedSites = async () => {
    const selectedSitesList = selectedAiSiteIndices.map(i => ({ ...aiSites[i], _idx: i }));
    if (selectedSitesList.length === 0) {
      setToast({ type: 'info', message: siteMessages.aiSelectOneSite });
      return;
    }
    let investigators = [];
    try {
      const invRes = await api.get('/investigators');
      investigators = invRes.data || [];
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to load investigators for validation.' });
      return;
    }
    const piNames = new Set(investigators.map(inv => (inv.fullName || inv.name || '').trim().toLowerCase()).filter(Boolean));
    const piNotFound = [];
    const hasBlankFields = [];
    for (const site of selectedSitesList) {
      const pi = (site.keyInvestigator || '').trim();
      if (!pi) {
        hasBlankFields.push({ ...site, reason: 'PI Name' });
        continue;
      }
      if (!piNames.has(pi.toLowerCase())) {
        piNotFound.push(pi);
      }
      const siteNameOk = (site.siteName || '').trim().length > 0;
      const countryOk = (site.country || '').trim().length > 0;
      if (!siteNameOk || !countryOk) {
        hasBlankFields.push({ ...site, reason: !siteNameOk && !countryOk ? 'Site Name, Country' : !siteNameOk ? 'Site Name' : 'Country' });
      }
    }
    if (piNotFound.length > 0) {
      setToast({
        type: 'error',
        message: `${siteMessages.aiPiNotInSystem} ${piNotFound.join(', ')}`,
      });
      return;
    }
    if (hasBlankFields.length > 0) {
      setToast({ type: 'info', message: siteMessages.aiRequiredFieldsBlank });
      setAddNewSiteInitialData({
        keyInvestigator: hasBlankFields[0].keyInvestigator || '',
        siteName: (hasBlankFields[0].siteName || '').trim(),
        country: (hasBlankFields[0].country || '').trim(),
      });
      setShowAddSiteModal(true);
      return;
    }
    eSignRemarksRef.current = '';
    setShowESignModal(true);
    eSignModeRef.current = 'aiDirect';
  };

  const runAfterESign = async () => {
    setShowESignModal(false);
    if (eSignModeRef.current === 'aiDirect') {
      eSignModeRef.current = 'proposed';
      await directSaveAiSuggestedSites();
      return;
    }
    const remark = (eSignRemarksRef.current || '').trim();
    onSave({
      sites: selectedSites,
      remark: remark || undefined,
    });
  };

  const directSaveAiSuggestedSites = async () => {
    const selectedSitesList = selectedAiSiteIndices.map(i => aiSites[i]).filter(Boolean);
    let countries = [];
    try {
      const countriesRes = await api.get('/countries');
      countries = countriesRes.data || [];
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to load countries.' });
      return;
    }
    const created = [];
    for (const site of selectedSitesList) {
      const countryName = (site.country || '').trim();
      const countryMatch = countries.find(c => (c.countryNiceName || '').trim() === countryName);
      const countryNo = countryMatch ? countryMatch.countryNo : null;
      const requestData = {
        projectCode: project,
        investigator: (site.keyInvestigator || '').trim(),
        investigatorEmail: null,
        siteName: (site.siteName || '').trim(),
        siteEmail: null,
        countryNo: countryNo != null ? (typeof countryNo === 'number' ? countryNo : parseInt(countryNo, 10)) : null,
        ecEmail: null,
        recruitmentProjections: null,
        isActive: true,
        remark: (eSignRemarksRef.current || '').trim() || null,
      };
      try {
        const response = await api.post('/sites', requestData);
        const s = response.data;
        created.push({
          siteId: s.siteId,
          siteNo: s.siteNo,
          piName: s.piName || site.keyInvestigator,
          siteName: s.siteName || site.siteName,
          siteAddress: s.siteAddress || site.country,
          therapeuticArea: aiTherapeuticArea || '',
          disease: aiDisease || '',
        });
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create site';
        setToast({ type: 'error', message: `${site.siteName || 'Site'}: ${msg}` });
        return;
      }
    }
    if (created.length > 0) {
      setSelectedAiSiteIndices([]);
      onSave(created);
      setToast({ type: 'success', message: 'AI suggested site(s) saved successfully.' });
      onClose();
    }
  };

  const handleAddNewSite = (siteData) => {
    // Add the new site to the suggested sites list for display
    const newSite = {
      ...siteData,
      siteId: siteData.siteNo,
      overallRating: 0, // New sites start with no rating
      highlighted: false,
    };
    setSuggestedSites(prev => [...prev, newSite]);
    
    // If parent component has onAddNewSite handler, add directly to main grid
    if (onAddNewSite) {
      onAddNewSite(siteData);
    } else {
      // Otherwise, auto-select the newly added site in the modal
      setSelectedSites(prev => {
        if (!prev.find(s => s.siteId === newSite.siteId)) {
          return [...prev, { ...newSite, siteNo: newSite.siteNo }];
        }
        return prev;
      });
    }
    // Close the Site Feasibility & Selection modal after saving the new site
    onClose();
  };

  const parseAiSitesFromRaw = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    let str = raw.trim();
    if (str.startsWith('```json')) str = str.replace(/^```json\s*/, '').replace(/\s*```$/, '').trim();
    else if (str.startsWith('```')) str = str.replace(/^```\s*/, '').replace(/\s*```$/, '').trim();
    const tryParse = (s) => {
      try {
        const parsed = JSON.parse(s);
        if (parsed && Array.isArray(parsed.sites) && parsed.sites.length > 0) return parsed.sites;
      } catch (_) {}
      return null;
    };
    let sites = tryParse(str);
    if (sites) return sites;
    // Repair truncated JSON (e.g. ends with "...infrastructure." missing }]} )
    const trimmed = str.trimEnd();
    if (trimmed.endsWith('"')) sites = tryParse(trimmed + '}]}');
    else if (trimmed.endsWith('}')) sites = tryParse(trimmed + ']}');
    else if (trimmed.endsWith(']')) sites = tryParse(trimmed + '}');
    else if (trimmed.length > 0) sites = tryParse(trimmed + '}]}');
    return sites || null;
  };

  const fetchAiSuggestedSites = async () => {
    if (!project) {
      setToast({ type: 'info', message: 'Select a project first to get AI suggestions.' });
      return;
    }
    if (!selectedCountryNos || selectedCountryNos.length === 0) {
      setToast({ type: 'info', message: 'Please select at least one country to get AI suggestions.' });
      return;
    }
    try {
      setAiLoading(true);
      setAiRawResponse(null);
      setAiIntegratedSourceErrorOnly(false);
      setSelectedAiSiteIndices([]);
      const response = await api.get(`/sites/feasibility/ai-suggested?project=${encodeURIComponent(project)}&countries=${encodeURIComponent(selectedCountryNos.join(','))}`);
      const data = response.data;
      let sites = (data.sites && Array.isArray(data.sites) ? data.sites : null) || (data.Sites && Array.isArray(data.Sites) ? data.Sites : null) || [];
      if (sites.length === 0 && (data.rawResponse || data.RawResponse)) {
        const raw = data.rawResponse ?? data.RawResponse;
        const parsed = parseAiSitesFromRaw(raw);
        if (parsed) sites = parsed;
      }
      const beforePiFilterCount = sites.length;
      sites = sites.filter((site) =>
        isLikelyPrincipalInvestigatorPersonName(site.keyInvestigator ?? site.KeyInvestigator)
      );
      aiLastFetchCountsRef.current = {
        beforePiFilter: beforePiFilterCount,
        afterPiFilter: sites.length,
      };
      let rawResponse = data.rawResponse ?? data.RawResponse ?? null;
      if (shouldSuppressAsRawAiSitesPayload(rawResponse)) {
        rawResponse = null;
      }
      const rawStr = typeof rawResponse === 'string' ? rawResponse : '';
      const isIntegratedSourceMsg = rawStr === siteMessages.aiIntegratedSourceUnavailable;
      const looksLikeProviderError =
        isIntegratedSourceMsg ||
        /API error|ServiceUnavailable|UNAVAILABLE|"code"\s*:\s*503/i.test(rawStr);
      const resolvedRawMessage = rawStr
        ? resolveAiSuggestedSitesUserFacingMessage(rawStr)
        : null;
      // Provider/integration errors: toast only, no inline grey box. Any other backend text: show mapped validation, never raw API/JSON.
      const rawForInline =
        rawResponse && looksLikeProviderError
          ? null
          : rawResponse
            ? resolvedRawMessage
            : null;

      setAiSites(sites);
      setAiInsights(data.insights ?? data.Insights ?? null);
      setAiRawResponse(rawForInline);
      setAiDisease(data.disease ?? data.Disease ?? '');
      setAiTherapeuticArea(data.therapeuticArea ?? data.TherapeuticArea ?? '');
      setAiFetched(true);
      if (beforePiFilterCount > 0 && sites.length === 0) {
        setToast({ type: 'info', message: siteMessages.aiSitesFilteredByPiName });
      } else if (sites.length === 0 && rawResponse) {
        const toastMsg = looksLikeProviderError
          ? siteMessages.aiIntegratedSourceUnavailable
          : resolvedRawMessage ?? siteMessages.aiSuggestionUnableToComplete;
        const softEmpty = toastMsg === siteMessages.aiSuggestionEmptyResponse;
        setToast({
          type: looksLikeProviderError || !softEmpty ? 'error' : 'info',
          message: toastMsg,
        });
        if (looksLikeProviderError || toastMsg !== siteMessages.aiSuggestionEmptyResponse) {
          setAiIntegratedSourceErrorOnly(true);
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      const apiMsg =
        (typeof data === 'string' ? data : data?.message) || err.message || '';
      const msg = resolveAiSuggestedSitesUserFacingMessage(String(apiMsg), { httpStatus: status });
      setToast({ type: 'error', message: msg });
      setAiIntegratedSourceErrorOnly(true);
      setAiSites([]);
      setAiInsights(null);
      setAiRawResponse(null);
      setAiDisease('');
      setAiTherapeuticArea('');
      setAiFetched(true);
    } finally {
      setAiLoading(false);
    }
  };

  const currentSites = suggestedSites;
  const filteredCurrentSites = useMemo(() => {
    const query = proposedSearchText.trim().toLowerCase();
    if (!query) return currentSites;
    return currentSites.filter((site) => {
      const r = Number(site.overallRating);
      const ratingNum = Number.isFinite(r) ? r : 0;
      const ratingFixed = ratingNum.toFixed(1);
      const ratingDisplay = `${ratingFixed} / 5`;
      return [
        site.piName,
        site.siteName,
        site.siteAddress,
        site.therapeuticArea,
        site.disease,
        ratingFixed,
        ratingDisplay,
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [currentSites, proposedSearchText]);

  const filteredAiSites = useMemo(() => {
    const query = recommendedSearchText.trim().toLowerCase();
    if (!query) return aiSites;
    return aiSites.filter((site) => {
      const hasScore = site.score != null && site.score !== '';
      const raw = hasScore ? Number(site.score) / 20 : NaN;
      const ratingNum = Number.isFinite(raw) ? raw : 0;
      const ratingFixed = ratingNum.toFixed(1);
      const ratingDisplay = `${ratingFixed} / 5`;
      const ratingSearchParts = hasScore && Number.isFinite(raw)
        ? [ratingFixed, ratingDisplay]
        : ['na'];
      return [
        site.keyInvestigator,
        site.siteName,
        site.country,
        site.therapeuticExpertise,
        aiDisease,
        ...ratingSearchParts,
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [aiSites, recommendedSearchText, aiDisease]);

  return (
    <>
    <div className="modal-overlay site-selection-overlay">
      <div className="modal-content site-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Site Feasibility & Selection</h3>
          <div className="header-actions">
            
            <button
              className="btn btn-primary"
              style={{ marginRight: '10px' }}
              onClick={() => setShowAddSiteModal(true)}
            >
              <FiPlus /> Add New Site
            </button>
            <button className="modal-close" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'proposed' ? 'active' : ''}`}
            onClick={() => setActiveTab('proposed')}
          >
            STS-LP Suggested Sites
          </button>
          <button
            className={`tab ${activeTab === 'recommended' ? 'active' : ''}`}
            onClick={() => setActiveTab('recommended')}
          >
            Recommended Sites (AI Suggested)
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content-area">
          {activeTab === 'recommended' ? (
            /* Recommended Sites = AI Suggested Sites */
            <div className={`ai-suggested-content ${!aiLoading && aiSites.length === 0 ? 'ai-suggested-content--with-dropdown-space' : ''}`}>
               
              
              
              <div className="ai-suggested-filters-row">
                <div className="ai-suggested-country-filter">
                  <label className="ai-suggested-country-label">Country <span className="text-danger">*</span></label>
                  <MultiSelectDropdown
                    placeholder="Select Country"
                    options={countryOptions}
                    selectedValues={selectedCountryNos}
                    onChange={setSelectedCountryNos}
                    inlineSummary={true}
                    searchable
                    searchPlaceholder="Search by country name…"
                  />
                  
                </div>
               
                <div className="ai-suggested-actions">
                  <button
                    className="btn btn-primary"
                    onClick={fetchAiSuggestedSites}
                    disabled={aiLoading || !project}
                    title={!project ? 'Select a project first' : 'Fetch AI suggestions'}
                  >
                    {aiLoading ? 'Loading…' : 'Get AI Suggestions'}
                  </button>
                </div>

                
              </div>
              {!aiLoading && aiSites.length > 0 && (
                <div className="site-searchbar-wrap">
                  <input
                    type="text"
                    className="form-input site-searchbar-input"
                    placeholder="Search"
                    value={recommendedSearchText}
                    onChange={(e) => setRecommendedSearchText(e.target.value)}
                  />
                </div>
              )}
              
              {aiLoading && <div className="loading-state">AI is Thinking…</div>}
              {!aiLoading &&
                aiFetched &&
                aiSites.length === 0 &&
                !aiRawResponse &&
                !aiIntegratedSourceErrorOnly &&
                aiLastFetchCountsRef.current.beforePiFilter === 0 &&
                aiLastFetchCountsRef.current.afterPiFilter === 0 && (
                  <div className="ai-suggested-empty">{siteMessages.aiNoSitesForSelectedCountries}</div>
                )}
              {!aiLoading && aiFetched && aiRawResponse && (!aiSites || aiSites.length === 0) && (
                <div className="ai-suggested-raw">{aiRawResponse}</div>
              )}
              {!aiLoading && aiSites.length > 0 && (
                <>
                  <div className="table-responsive">
                    <table className="sites-selection-table ai-suggested-table">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>PI Name</th>
                          <th>Site Name</th>
                          <th>Site Address</th>
                          <th>Therapeutic Area</th>
                          <th>Disease</th>
                          <th className="text-center">Overall Rating</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAiSites.length > 0 ? (
                          filteredAiSites.map((site) => {
                            const originalIdx = aiSites.indexOf(site);
                            return (
                              <tr key={`${site.siteName || 'site'}-${originalIdx}`}>
                                <td className="text-center">
                                  <input
                                    type="checkbox"
                                    title="Select site"
                                    checked={isAiSiteSelected(originalIdx)}
                                    onChange={(e) => handleAiCheckboxChange(originalIdx, e.target.checked)}
                                  />
                                </td>
                                <td>{site.keyInvestigator || '—'}</td>
                                <td>{site.siteName}</td>
                                <td>{site.country}</td>
                                <td>{site.therapeuticExpertise}</td>
                                <td>{aiDisease || '—'}</td>
                                <td className="text-center">
                                  {site.score != null ? (
                                    <span title={getOverallRatingTooltip(site, true)}>
                                      <StarRating rating={Number(site.score) / 20} showValue={true} />
                                    </span>
                                  ) : (
                                    'NA'
                                  )}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleViewAiSuggestedCriteria(site)}
                                  >
                                    View Criteria Details
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={8} className="empty-state-cell">
                              No sites found matching your search.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ) : loading ? (
            <div className="loading-state">Loading sites...</div>
          ) : (
            <>
              <div className="sts-lp-search-legend-row">
                <div className="site-searchbar-wrap sts-lp-search-legend-row__search">
                  <input
                    type="text"
                    className="form-input site-searchbar-input"
                    placeholder="Search"
                    value={proposedSearchText}
                    onChange={(e) => setProposedSearchText(e.target.value)}
                  />
                </div>
                <div
                  className="sts-lp-row-legend sts-lp-row-legend--inline"
                  aria-label="STS-LP suggested sites row colors"
                >
                  <span className="sts-lp-row-legend-item">
                    <span
                      className="sts-lp-row-legend-swatch sts-lp-row-legend-swatch--site-master"
                      aria-hidden
                    />
                    <span>Selected Sites</span>
                  </span>
                </div>
              </div>
              <div className="table-responsive">
                <table className="sites-selection-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>PI Name</th>
                      <th>Site Name</th>
                      <th>Site Address</th>
                      <th>Therapeutic Area</th>
                      <th>Disease</th>
                      <th className="text-center">Overall Rating</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCurrentSites.length > 0 ? (
                      filteredCurrentSites.map((site) => {
                        const isAlreadySelected = alreadySelectedSiteIds.includes(site.siteId);
                        const isGreenRow =
                          isAlreadySelected || Boolean(site.highlighted);
                        return (
                          <tr
                            key={site.siteId}
                            className={isAlreadySelected ? 'already-selected-row' : (site.highlighted ? 'highlighted_row' : '')}
                          >
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={isSiteSelected(site.siteId)}
                                disabled={isGreenRow}
                                onChange={(e) => handleCheckboxChange(site.siteId, e.target.checked)}
                                title={
                                  isAlreadySelected
                                    ? 'This site is already on the feasibility list'
                                    : site.highlighted
                                      ? ''
                                      : ''
                                }
                              />
                            </td>
                            <td>{site.piName}</td>
                            <td>{site.siteName}</td>
                            <td>{site.siteAddress}</td>
                            <td>{site.therapeuticArea}</td>
                            <td>{site.disease}</td>
                            <td className="text-center">
                              <span title={getOverallRatingTooltip(site, false)}>
                                <StarRating rating={site.overallRating || 0} showValue={true} />
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleViewRating(site)}
                              >
                                View Criteria Details
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : suggestedSites.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="empty-state-cell">
                          No rows in STS-LP import for this project. Upload a CSV from the STS-LP CSV import page
                          in the menu (project code must match the header project, e.g. 0198-23), then close and
                          reopen Site Selection.
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={8} className="empty-state-cell">
                          No sites found matching your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && selectedSiteForRating && (
        <SiteRatingModal
          site={selectedSiteForRating}
          onClose={() => {
            setShowRatingModal(false);
            setSelectedSiteForRating(null);
          }}
        />
      )}

      {selectedAiSiteForCriteria && (
        <AiSuggestedSiteCriteriaModal
          site={selectedAiSiteForCriteria}
          diseaseLabel={aiDisease}
          therapeuticAreaLabel={aiTherapeuticArea}
          onClose={() => setSelectedAiSiteForCriteria(null)}
        />
      )}

      {/* Add New Site Modal (initialData when opened from AI Suggested tab with blank/incomplete data) */}
      {showAddSiteModal && (
        <AddNewSiteModal
          project={project}
          initialData={addNewSiteInitialData}
          onClose={() => {
            setShowAddSiteModal(false);
            setAddNewSiteInitialData(null);
          }}
          onSave={handleAddNewSite}
        />
      )}

      {/* eSign Modal - when saving suggested sites */}
      {showESignModal && (
        <ElectronicSign
          show={true}
          onClose={() => { setShowESignModal(false); eSignRemarksRef.current = ''; }}
          onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
          onSave={runAfterESign}
        />
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </div>

    <AddInvestigatorModal
      isOpen={showInvestigatorModal}
      onClose={() => setShowInvestigatorModal(false)}
    />
    </>
  );
};

export default SiteSelectionModal;
